import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { ToolbarAction } from '../se-toolbar/se-buttons-toolbar/types';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import { firstValueFrom, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfirmDialogService } from '../confirm/confirm-dialog.service';
import { EstensaSignService } from '../sign/estensasign.service';
import {
    assertIsSissCloudSignRequestData,
    EcgChartState,
    IScrybaCertificate,
    ISeSignResultData,
    isSissCloudSignRequestData,
    ReportType,
    SeConfirmoSignInfo,
    SeMultiSignInfo,
    SeSignInfo,
    SeSignRequestData,
    SeSissCloudSignInfo,
    SeSissCloudSignRequestData,
    SeSissSignRequestData,
    SeSissWayCloudSignInfo,
    SignType,
    SissData,
} from '../models/estensasign.model';
import { RootState } from '../root-store/root.state';
import {
    sePrintEcgPdfOnModal,
    seShowEcgPdfOnModal,
    seShowPreviewEcgPdfOnModal,
    seShowVisitPdfCertificateOnModal,
    seShowVisitPdfOnModal,
} from '../pdf-modal/redux/pdf-modal.actions';
import { EstensaWaitingService } from './waiting.service';
import { RadioSettingsService } from './settings/radio-settings.service';
import { seLockedExams } from '../root-store/root.actions';
import {
    createSignificantImagesErrorMessage,
    isExceptionCredentialFault,
    isResponseServerError,
    isResponseSignErrored,
    isResponseSignificantImagesError,
    isResponseSignLocked,
    isResponseSignOk,
} from '../utils/report-routines';
import { AuthenticationService } from './authentication/authentication.service';
import {
    Addendum,
    IReport,
    IReportWorkflowExamService,
    IResponseStatus,

} from './report-workflow-exam.service';
import { SissCloudService } from '../sign/siss-cloud/siss-cloud.service';
import { ModalDialogNotificationService, NotificationService, NotificationType } from './notification.service';
import { ButtonClick, ModalWindowSize } from '../dialogs/se-modalwindow/se-modalwindow.component';
import { ServerRootService } from '../utils/routing/serverroot.service';
import { Logger } from '@se/se.platform.base';
import { Burner } from '../models/mps.models';
import { burnData, VisitExamination } from '../models/visit';
import { ISpecialtyExamination } from '../models/specialty';
import { EcgExamination } from '../models/ecg';
import { ECGState } from 'projects/ecg-viewer-html5/src/store/reducers/ecg-viewer.reducer';
import { selectorChartState } from 'projects/ecg-viewer-html5/src/store/selectors/ecg-viegew.selectors';
import { ReportCoreService } from './rest/report-core.service';
import { AuthData, IdReportItem, SignResultStatus } from '../models';
import { VisitService } from './rest/visit.service';
import { IPatientReportPair, IReportContext } from '../models/report.models';
import { MassiveApproveParams, MassiveSignParams, SignOptions } from '../models/sign.models';

// https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-2.html#support-for-newtarget
export class ApprovalError extends Error {
    constructor(
        message: string,
        public operationCount: string,
        public operationFailedCount: string,
        public notificationType: 'warn',
    ) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

@Injectable({ providedIn: 'root' })
export class ReportWorkflowService extends ServerRootService {
    private _reportContext: IReportContext | null = null;
    private _authData: AuthData | AuthData[] | null = null;

    get authData(): AuthData | AuthData[] | null {
        return this._authData;
    }

    set authData(data: AuthData | AuthData[] | null) {
        this._authData = data;
    }

    cdaReports: Map<string, { isUndoSuccessfull: boolean; reportDetailsForNotification: string }> = new Map();
    cdaReportsSignError: Map<string, any> = new Map();

    // constructor

    constructor(
        private ecgStore: Store<ECGState>,
        private authenticationService: AuthenticationService,
        private confirmDialogService: ConfirmDialogService,
        private logger: Logger,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private notificationService: NotificationService,
        private reportCoreService: ReportCoreService,
        private settingsService: RadioSettingsService,
        private signService: EstensaSignService,
        private sissCloudService: SissCloudService,
        private store: Store<RootState>,
        private translate: TranslateService,
        private waitingService: EstensaWaitingService,
        private visitRestService: VisitService,
    ) {
        super();
    }

    // public functions

    setContext(reportContext: IReportContext | null) {
        this._reportContext = reportContext;
    }

    async annulment(examService: IReportWorkflowExamService, reports: IReport[], userId: string) {
        return this.confirmDialogService.beginConfirmationFlow({
            onConfirmCallback: (authData) =>
                this.onApproveCredentialsReceived(examService, authData, reports, false, true, undefined, true),
            userId,
            successMessageKey: 'AnnulmentOperationSuccess',
            errorMessageKey: 'ErrorDuringAnnulment',
            partialMessageKey: 'PartiallyAnnulmentDocOnTotal',
        });
    }

    async annulmentAndDelete(
        examService: IReportWorkflowExamService,
        report: IReport,
        patientReportPair: IPatientReportPair[],
        user: { id: string; name: string },
    ) {
        const shouldAnnul = report && (report.isApproved || report.isDigitallySigned);
        const message = shouldAnnul
            ? `${this.translate.instant('OnAnnulmentAndDeleteReportAlert')}\n${this.translate.instant('OnAnnulmentAndDeleteReport')}`
            : this.translate.instant('OnDeleteReport');
        const confirmation: ButtonClick = await firstValueFrom(
            this.modalDialogNotificationService.notify({
                buttonYes: true,
                buttonNo: true,
                buttonCancel: false,
                buttonOk: false,
                title: this.translate.instant('Warning'),
                message,
                type: NotificationType.warning,
                windowSize: ModalWindowSize.seMessageBox,
            }),
        );
        if (confirmation !== ButtonClick.yes) {
            return;
        }

        let promise = Promise.resolve();
        if (shouldAnnul && report.isDigitallySigned) {
            const signRequestData: SeSignRequestData = await this.buildDataSign(
                examService,
                [report],
                user,
                patientReportPair,
            );
            promise = this.sign(examService, signRequestData, { isDeferred: true, print: false });
        } else if (shouldAnnul && report.isApproved) {
            promise = this.annulment(examService, [report], user.id);
        }

        // Lascia che gli errori vengano gestiti dal processo di annullativo.
        await promise;

        try {
            const disconnectReport: IdReportItem = { idReport: report.id };
            await firstValueFrom(this.reportCoreService.disconnectReport(disconnectReport));
            this._reportContext?.refresh();
        } catch (error) {
            this.logger.error(error);
            this.modalDialogNotificationService.warning('OnAnnulmentAndDeleteReportError');
        }
    }

    async askAuthenticationConfirm(userId: string, forceAuthentication: boolean = false) {
        let isAuthenticationRequired: boolean = forceAuthentication;

        if (isAuthenticationRequired) {
            return this.confirmDialogService.beginConfirmationFlow({
                onConfirmCallback: async (authData) => authData,
                userId,
                successMessageKey: 'ReportApproved',
                errorMessageKey: 'ErrorDuringApproving',
                partialMessageKey: 'PartiallyApprovedDocOnTotal',
            });
        }
    }

    async massiveApprove(params: MassiveApproveParams) {
        const { userId } = params;
        /** utente loggato */
        const actualUserId = this.authenticationService.getUserId();
        let isAuthenticationRequired: boolean =
            /** se il refertatore non è l'utente loggato l'autenticazione è sempre richiesta */
            userId !== actualUserId ||
            !!this.settingsService.radioSettings?.reportSettings.isAuthenticationRequiredForVNEXTApprove;

        if (isAuthenticationRequired) {
            return this.confirmDialogService.beginConfirmationFlow({
                onConfirmCallback: (authData) => this.onApproveMassiveCredentialsReceived(params, authData),
                userId: params.userId,
                successMessageKey: 'ReportApproved',
                errorMessageKey: 'ErrorDuringApproving',
                partialMessageKey: 'PartiallyApprovedDocOnTotal',
                skipSuccessDialog: false,
            });
        }

        return this.onApproveMassiveCredentialsReceived(params, undefined);
    }

    async approve(
        examService: IReportWorkflowExamService,
        reports: IReport[],
        userId: string, // referringPhysicianId
        print,
        addendum?: Addendum,
        isFromWorklist?: boolean,
        displayedSettings?: EcgChartState,
        isReportOnDDevice?: boolean,
    ) {
        /** utente loggato */
        const actualUserId = this.authenticationService.getUserId();
        let isAuthenticationRequired: boolean =
            /** se il refertatore non è l'utente loggato l'autenticazione è sempre richiesta */
            userId !== actualUserId ||
            !!this.settingsService.radioSettings?.reportSettings.isAuthenticationRequiredForVNEXTApprove;
        if (isAuthenticationRequired) {
            return this.confirmDialogService.beginConfirmationFlow({
                onConfirmCallback: (authData) =>
                    this.onApproveCredentialsReceived(
                        examService,
                        authData,
                        reports,
                        print ? print : false,
                        false,
                        addendum,
                        isFromWorklist,
                        displayedSettings,
                        userId,
                        isReportOnDDevice,
                    ),
                userId,
                successMessageKey: 'ReportApproved',
                errorMessageKey: 'ErrorDuringApproving',
                partialMessageKey: 'PartiallyApprovedDocOnTotal',
            });
        } else {
            return this.onApproveCredentialsReceived(
                examService,
                undefined,
                reports,
                print ? print : false,
                false,
                addendum,
                isFromWorklist,
                displayedSettings,
                userId,
                isReportOnDDevice,
            );
        }
    }

    async approveMulti(
        examService: IReportWorkflowExamService,
        reports: IReport[],
        userIds: string[],
        print = false,
        addendum?: Addendum,
        isFromWorklist?: boolean,
        displayedSettings?: EcgChartState,
    ) {
        const actualUserId = this.authenticationService.getUserId();
        const isAllActualUserId = userIds.every((val) => val === actualUserId);
        const isAuthenticationRequired: boolean =
            /** se il refertatore di ogni visita non è l'utente loggato l'autenticazione è sempre richiesta */
            !isAllActualUserId ||
            !!this.settingsService.radioSettings?.reportSettings.isAuthenticationRequiredForVNEXTApprove;

        if (isAuthenticationRequired) {
            return this.confirmDialogService.beginMultiConfirmationFlow({
                onConfirmCallback: (authData: AuthData, otherAuthData) =>
                    this.onApproveMultiCredentialsReceived(
                        examService,
                        [authData, ...(otherAuthData || [])],
                        reports,
                        print,
                        false,
                        addendum,
                        isFromWorklist,
                        displayedSettings,
                    ),
                userIds,
                successMessageKey: 'ReportApproved',
                errorMessageKey: 'ErrorDuringApproving',
                partialMessageKey: 'PartiallyApprovedDocOnTotal',
                skipSuccessDialog: false,
            });
        } else {
            return this.onApproveMultiCredentialsReceived(
                examService,
                [],
                reports,
                print,
                false,
                addendum,
                isFromWorklist,
                displayedSettings,
            );
        }
    }

    async undoMultiValidation(
        examService: IReportWorkflowExamService,
        reports: IReport[],
        userIds: string[],
        isFromWorklist: boolean,
    ) {
        /** utente loggato */
        const actualUserId = this.authenticationService.getUserId();
        const isAllActualUserId = userIds.every((val) => val === actualUserId);
        const isAuthenticationRequired: boolean =
            /** se il refertatore di ogni visita non è l'utente loggato l'autenticazione è sempre richiesta */
            !isAllActualUserId ||
            !!this.settingsService.radioSettings?.reportSettings.isAuthenticationRequiredForVNEXTApprove;

        if (isAuthenticationRequired) {
            return this.confirmDialogService.beginMultiConfirmationFlow({
                onConfirmCallback: (authData: AuthData, otherAuthData) =>
                    this.onUndoValidationMultiCredentialsReceived(
                        examService,
                        [authData, ...(otherAuthData || [])],
                        reports,
                        isFromWorklist,
                    ),
                userIds,
                successMessageKey: 'UndoValidationOperationSuccess',
                errorMessageKey: 'UndoValidationOperationError',
                partialMessageKey: 'PartiallyUndoApprovedDocOnTotal',
                skipSuccessDialog: false,
            });
        }

        return this.onUndoValidationMultiCredentialsReceived(examService, undefined, reports, isFromWorklist);
    }

    async undoValidation(
        examService: IReportWorkflowExamService,
        reports: IReport[],
        userId: string,
        reportId: string,
        isFromWorklist: boolean,
    ) {
        /** utente loggato */
        const actualUserId = this.authenticationService.getUserId();
        let isAuthenticationRequired: boolean =
            /** se il refertatore non è l'utente loggato l'autenticazione è sempre richiesta */
            userId !== actualUserId ||
            !!this.settingsService.radioSettings?.reportSettings.isAuthenticationRequiredForVNEXTApprove;
        if (isAuthenticationRequired) {
            return this.confirmDialogService.beginConfirmationFlow({
                onConfirmCallback: async (authData) =>
                    await this.onUndoValidationCredentialsReceived(
                        examService,
                        authData,
                        reports,
                        reportId,
                        isFromWorklist,
                    ),
                userId,
                successMessageKey: 'UndoValidationOperationSuccess',
                errorMessageKey: 'UndoValidationOperationError',
                partialMessageKey: 'PartiallyUndoApprovedDocOnTotal',
            });
        } else {
            return this.onUndoValidationCredentialsReceived(examService, undefined, reports, reportId, isFromWorklist);
        }
    }

    async undoValidationForCDA(
        examService: IReportWorkflowExamService,
        reports: IReport[],
        userId: string,
        reportId: string,
        isFromWorklist: boolean,
    ) {
        /** utente loggato */
        //const actualUserId = this.authenticationService.getUserId();

        let isAuthenticationRequired: boolean =
            !!this.settingsService.radioSettings?.reportSettings.isAuthenticationRequiredForVNEXTApprove;
        if (isAuthenticationRequired && !this._authData) {
            return this.confirmDialogService.beginConfirmationFlowForCda({
                onConfirmCallback: async (authData) =>
                    await this.onUndoValidationCredentialsReceived(
                        examService,
                        authData,
                        reports,
                        reportId,
                        isFromWorklist,
                        true,
                    ),
                userId,
                successMessageKey: 'UndoValidationOperationSuccess',
                errorMessageKey: 'UndoValidationOperationError',
                partialMessageKey: 'PartiallyUndoApprovedDocOnTotal',
            });
        } else if (isAuthenticationRequired && this._authData) {
            await this.onUndoValidationCredentialsReceived(
                examService,
                this._authData as AuthData,
                reports,
                reportId,
                isFromWorklist,
                true,
            );
        }
    }

    async undoMultiValidationForCda(
        examService: IReportWorkflowExamService,
        reports: IReport[],
        userIds: string[],
        isFromWorklist: boolean,
    ) {
        /** utente loggato */
        const actualUserId = this.authenticationService.getUserId();
        const isAllActualUserId = userIds.every((val) => val === actualUserId);
        const isAuthenticationRequired: boolean =
            /** se il refertatore di ogni visita non è l'utente loggato l'autenticazione è sempre richiesta */
            !isAllActualUserId ||
            !!this.settingsService.radioSettings?.reportSettings.isAuthenticationRequiredForVNEXTApprove;

        if (isAuthenticationRequired && !this._authData) {
            return this.confirmDialogService.beginMultiConfirmationFlow({
                onConfirmCallback: async (authData: AuthData, otherAuthData) =>
                    await this.onUndoValidationMultiCredentialsReceived(
                        examService,
                        [authData, ...(otherAuthData || [])],
                        reports,
                        isFromWorklist,
                    ),
                userIds,
                successMessageKey: 'UndoValidationOperationSuccess',
                errorMessageKey: 'UndoValidationOperationError',
                partialMessageKey: 'PartiallyUndoApprovedDocOnTotal',
                skipSuccessDialog: false,
            });
        } else if (isAuthenticationRequired && this._authData) {
            await this.onUndoValidationMultiCredentialsReceived(
                examService,
                this._authData as AuthData[],
                reports,
                isFromWorklist,
            );
        }
    }

    getEcgChartState(): EcgChartState {
        //const chartState = this.ecgStore.getState().chartState;
        const chartState = this.ecgStore.selectSignal(selectorChartState)();
        return {
            cabrera: !!chartState.format,
            disposition: chartState.arrangement,
            markerDisplay: chartState.fiducialPointsVisible,
            paperOpacity: chartState.paperOpacity,
            paperSpeed: chartState.paperRate,
            sequential: !chartState.timeAlignment,
            space: chartState.traceSpacing,
            printAsDisplayed: chartState.printAsDisplayed,
            // ReportKind: "page_1",
            // Rhythm: 4
        };
    }

    previewAeEcgReports(examinationId: string) {
        this.store.dispatch(seShowPreviewEcgPdfOnModal({ examinationId }));
    }

    previewEcgReports(reportIds: string[], displayedSettings?: EcgChartState) {
        this.store.dispatch(seShowEcgPdfOnModal({ reportIds, displayedSettings })); // Notifico lo stato
    }

    previewVisitReports(reportIds: string[], displayedSettings?: EcgChartState) {
        this.store.dispatch(seShowVisitPdfOnModal({ reportIds, displayedSettings })); // Notifico lo stato
    }

    previewVisitCertificate(reportIds: string[], smCertificateCode: string) {
        this.store.dispatch(seShowVisitPdfCertificateOnModal({ reportIds, smCertificateCode })); // Notifico lo stato
    }

    async burnCd(
        examService: IReportWorkflowExamService,
        selectedItems: burnData[],
        burner?: Burner,
    ): Promise<boolean> {
        let publicationExams = selectedItems.filter((item) => item.publicationAuthorization);
        if (publicationExams.length > 0) {
            let messageForPublication = '';
            if (selectedItems.length === 1) {
                messageForPublication = 'PatientCDSingleForPublication';
            } else if (selectedItems.length !== publicationExams.length) {
                messageForPublication = 'PatientCDSomeForPublication';
            } else {
                messageForPublication = 'PatientCDAllForPublication';
            }
            let buttonClick: ButtonClick = await firstValueFrom(
                this.modalDialogNotificationService.notify({
                    buttonYes: true,
                    buttonNo: true,
                    buttonCancel: false,
                    buttonOk: false,
                    title: this.translate.instant('Warning'),
                    message: this.translate.instant(messageForPublication),
                    type: NotificationType.warning,
                }),
            );

            if (buttonClick !== ButtonClick.yes) {
                return false;
            }
        }

        let idExaminations = selectedItems.map((exam) => exam.id);
        let isBurnAccepted: boolean = false;
        let result: any;

        this.waitingService.isWaiting = false;
        setTimeout(() => (this.waitingService.isWaiting = true), 0);
        try {
            result = await firstValueFrom(examService.burnCd(idExaminations, burner));
        } catch (error) {
            console.error(error);
            result = {
                accepted: false,
                message: this.translate.instant('MPSBurner') + ' - ' + this.translate.instant('PatientCDError'),
            };
        } finally {
            setTimeout(() => (this.waitingService.isWaiting = false), 0);
        }

        const notification = {
            buttonYes: false,
            buttonNo: false,
            buttonCancel: false,
            buttonOk: true,
            title: this.translate.instant('MPSBurner') + ' - ' + this.translate.instant('Warning'),
            message: this.translate.instant(result.message),
            type: NotificationType.warning,
        };
        if (result.accepted) {
            isBurnAccepted = true;
            notification['title'] = this.translate.instant('MPSBurner') + ' - ' + this.translate.instant('Information');
            notification['type'] = NotificationType.information;
        }

        await firstValueFrom(this.modalDialogNotificationService.notify(notification));

        return isBurnAccepted;
    }

    printReports(reports: IReport[], displayedSettings?: EcgChartState) {
        const idReports = reports.filter((item) => item.isApproved).map((item) => item.id);

        let reportCount = reports.length; // numero di report di cui si chiede la stampa
        let reportedCount = reports.filter((item) => !item.isApproved).length; // Estraggo il numero report in stato R => (se non è definitivo, non è nemmeno firmato, quindi non serve inserire una condizione)

        if (reportedCount > 0 && reportedCount < reportCount) {
            // Se ci sono dei report solo refertati, ma il numero di questi report è inferiore al totale allora procedo alla stampa con warning
            this.modalDialogNotificationService
                .warning(this.translate.instant('ECG_PrintOnlyApprovedAndSigned'))
                .subscribe(() => {
                    this.printSelectedReports(idReports, displayedSettings);
                });
        } else if (reportedCount <= 0 && reportCount > 0) {
            // Se sono tutti definitvi o firmati, stampo senza warning
            this.printSelectedReports(idReports, displayedSettings);
        }
    }

    /*83782*/
    async buildMultiToOneDataSign(
        report: IReport,
        signers: {
            id: string;
            name: string;
        }[],
    ): Promise<SeSignRequestData[]> {
        //trim signers data
        signers.forEach((signer) => {
            if (signer.name) signer.name = signer.name.trim();
        });

        return signers.map((signer) => ({
            signerName: signer.name,
            signerId: signer.id,
            reportIdList: [report.id.toString()],
            reportErrorDetailList: [{ id: report.id, message: report.reportDetailForNotification }],
        }));

        /*83782
         * vedi buildDataSign per codice legato agl altri provider di firma certificata
         * quando andranno implementati va adattato alla firma multipla*/
    }

    async buildDataSign(
        examService: IReportWorkflowExamService,
        reports: IReport[],
        signer: {
            id: string;
            name: string;
        },
        patientReportPair: IPatientReportPair[],
    ): Promise<SeSignRequestData> {
        let idList = reports.map((item) => item.id.toString());
        let reportErrorDetailList = reports.map((item) => {
            return { id: item.id, message: item.reportDetailForNotification };
        });

        // A volte arriva con spazi iniziali...
        if (signer.name) {
            signer.name = signer.name.trim();
        }

        switch (this.signService.currentSignType) {
            case SignType.SISSWay:
            case SignType.SISS:
                return this.buildSissDataSign(examService, signer, patientReportPair, idList, reportErrorDetailList);
            case SignType.SISSCloud:
            case SignType.SISSWayCloud:
                return this.buildSissCloudDataSign(
                    examService,
                    signer,
                    patientReportPair,
                    idList,
                    reportErrorDetailList,
                );
            default:
                return {
                    signerName: signer.name,
                    signerId: signer.id,
                    reportIdList: idList,
                    reportErrorDetailList: reportErrorDetailList,
                };
        }
    }

    /*83782*/
    async multiSign(examService: IReportWorkflowExamService, signRequestData: SeSignRequestData[]) {
        for (const data of signRequestData) {
            // Operazioni preliminari all'apertura della finestra di firma
            switch (this.signService.currentSignType) {
                case SignType.ScrybaSign:
                    let certificates: IScrybaCertificate[] = [];

                    this.waitingService.isWaiting = true;
                    certificates = await firstValueFrom(this.visitRestService.getScrybaCertificate(data.signerId));
                    this.waitingService.isWaiting = false;
                    if (!Array.isArray(certificates) || certificates[0]?.code == 'error') {
                        this.modalDialogNotificationService.error(
                            this.translate.instant('ScrybaSign_CertificatesNotFound'),
                        );
                        if (Array.isArray(certificates) && !!certificates[0]?.description)
                            this.notificationService.error(
                                certificates[0].description,
                                this.translate.instant('ScrybaSign_CertificatesNotFound'),
                            );
                        return Promise.reject<void>();
                    }

                    data.extraData = certificates;
                    break;
                case SignType.Confirmo:
                    this.waitingService.isWaiting = true;
                    try {
                        let confirmoInfo: SeConfirmoSignInfo = await firstValueFrom(
                            this.visitRestService.getConfirmoSignConfiguration(data.signerId),
                        );
                        data.extraData = confirmoInfo;
                    } catch {
                        this.modalDialogNotificationService.error(this.translate.instant('Sign_InitializationError'));
                        return Promise.reject<void>();
                    } finally {
                        this.waitingService.isWaiting = false;
                    }

                    break;
                default:
                    //Do nothing
                    break;
            }
        }

        switch (this.signService.currentSignType) {
            case SignType.CompedDigitalSign:
                return  this.signService.doubleSignComped(
                    signRequestData,
                    async (info: SeSignInfo): Promise<ISeSignResultData[]> => {
                        return firstValueFrom(examService.sign(info)).then((list) => {
                            this._reportContext?.refresh();
                            return list;
                        });
                    },
                    async (info: SeSignInfo) => {
                        return firstValueFrom(examService.getPdf(info)).then((list: ISeSignResultData[]) => {
                            return list;
                        });
                    },
                );
            default:
                return new Promise<void>((resolve, reject) => {
                    if (typeof examService.multiSign !== 'function') {
                        return;
                    }
                    this.signService.beginMultiSigningFlow(
                        signRequestData,
                        async (info: SeMultiSignInfo): Promise<ISeSignResultData[]> => {
                    
                            switch (this.signService.currentSignType) {
                                case SignType.InfoCert:
                                case SignType.ARUBA:
                                case SignType.Confirmo:
                                    return firstValueFrom(examService.multiSign(info)).then((list) => {
                                        this._reportContext?.refresh();
                                        return list;
                                    });
                                default:
                                    return Promise.reject({
                                        message: `${SignType[this.signService.currentSignType]} ${this.translate.instant('DigitalSign_SignNotSupported')}`,
                                    });
                            }
                        },
                        async (info: SeSignInfo) => {
                            return firstValueFrom(examService.getPdf(info)).then((list: ISeSignResultData[]) => {
                                return list;
                            });
                        },
                        (err) => {
                            if (err) {
                                reject(err);
                            } else {
                                resolve();
                            }
                        },
                    );
                });

        }

    }

    async massiveSign(params: MassiveSignParams, fromCda = false) {
        const { ecgSignData, visitSignData, print, addendum, displayedSettings, isDeferred, ecgService, visitService } =
            params;

        await this.preFlowOps(params.ecgService, params.ecgSignData, isDeferred);
        await this.preFlowOps(params.visitService, params.visitSignData, isDeferred);

        let _extraData: IScrybaCertificate[] | SeConfirmoSignInfo | null = null;
        switch (this.signService.currentSignType) {
            case SignType.ScrybaSign:
                _extraData = await firstValueFrom(
                    this.visitRestService.getScrybaCertificate(ecgSignData.signerId || visitSignData.signerId),
                );
                if (!Array.isArray(_extraData) || _extraData[0]?.code == 'error') {
                    this.modalDialogNotificationService.error(
                        this.translate.instant('ScrybaSign_CertificatesNotFound'),
                    );
                    if (Array.isArray(_extraData) && !!_extraData[0]?.description)
                        this.notificationService.error(
                            _extraData[0].description,
                            this.translate.instant('ScrybaSign_CertificatesNotFound'),
                        );

                    return Promise.reject<void>();
                }
                break;
            case SignType.Confirmo:
                this.waitingService.isWaiting = true;
                try {
                    _extraData = await firstValueFrom(
                        this.visitRestService.getConfirmoSignConfiguration(
                            ecgSignData.signerId || visitSignData.signerId,
                        ),
                    );
                } catch {
                    this.modalDialogNotificationService.error(this.translate.instant('Sign_InitializationError'));
                    return Promise.reject<void>();
                } finally {
                    this.waitingService.isWaiting = false;
                }
                break;
        }

        // Attenzione a possibili proprietà di classi che estendono SeSignRequestData: se non aggiunte qua vengono
        //   perse...
        const signRequestData = {
            signerName: ecgSignData.signerName || visitSignData.signerName,
            signerId: ecgSignData.signerId || visitSignData.signerId,
            reportIdList: [...ecgSignData.reportIdList, ...visitSignData.reportIdList],
            initialErrorMessage: ecgSignData.initialErrorMessage || visitSignData.initialErrorMessage,
            reportErrorDetailList: [...ecgSignData.reportErrorDetailList, ...visitSignData.reportErrorDetailList],
            sissDataInfoList: [],
            extraData: _extraData,
        } satisfies SeSignRequestData & SeSissSignRequestData & SeSissCloudSignRequestData;

        if (isSissCloudSignRequestData(ecgSignData)) {
            signRequestData.sissDataInfoList.push(...ecgSignData.sissDataInfoList);
        }
        if (isSissCloudSignRequestData(visitSignData)) {
            signRequestData.sissDataInfoList.push(...visitSignData.sissDataInfoList);
        }

        return new Promise<void>((resolve, reject) => {
            this.signService.beginSigningFlow(
                signRequestData,
                async (info: SeSignInfo): Promise<ISeSignResultData[]> => {
                    if (addendum) {
                        await this.handleAddendum(ecgService, addendum);
                        await this.handleAddendum(visitService, addendum);
                    }

                    info.isDeferred = !!isDeferred;
                    if (!!displayedSettings) {
                        info.displayedSettings = displayedSettings;
                    }

                    let visitSignResult: ISeSignResultData[];
                    let ecgSignResult: ISeSignResultData[];
                    switch (this.signService.currentSignType) {
                        case SignType.SISSWay:
                        case SignType.SISS:
                        case SignType.CompedDigitalSign:
                        case SignType.InfoCert:
                            // Firma delle visite
                            if (info.info) {
                                info.info.reportIdList = visitSignData.reportIdList;
                            }
                            visitSignResult = await firstValueFrom(visitService.sign(info));

                            // Firma degli ECG
                            if (info.info) {
                                info.info.reportIdList = ecgSignData.reportIdList;
                            }
                            ecgSignResult = await firstValueFrom(ecgService.sign(info));

                            const list = [...visitSignResult, ...ecgSignResult];
                            if (list.length) {
                                this._reportContext?.refresh();
                            }

                            if (print) {
                                let signedReportIds = list.filter((item) => item.signed).map((item) => item.reportId);
                                this.printSelectedReports(signedReportIds);
                            }
                            if (list.length && fromCda) {
                                list.forEach((item) => {
                                    this.cdaReportsSignError.set(item.reportId, {
                                        serverError: item.serverError,
                                        serverErrorDetail: item.serverErrorDetail,
                                    });
                                });
                            }
                            return list;
                        case SignType.InsielARSS:
                        case SignType.ScrybaSign:
                        case SignType.ARUBA:
                        case SignType.Confirmo:
                            // Firma visite + ECG contestuale
                            if (info.info) {
                                info.info.reportIdList = [...visitSignData.reportIdList, ...ecgSignData.reportIdList];
                            }

                            info.visitCount = visitSignData.reportIdList.length;
                            info.ecgCount = ecgSignData.reportIdList.length;
                            info.isVisitAndEcgSign = true;
                            const visitAndEcgSignResult = await firstValueFrom(visitService.sign(info));

                            if (print) {
                                let signedReportIds = visitAndEcgSignResult
                                    .filter((item) => item.signed)
                                    .map((item) => item.reportId);
                                this.printSelectedReports(signedReportIds);
                            }
                            if (visitAndEcgSignResult.length) {
                                this._reportContext?.refresh();
                                visitAndEcgSignResult.forEach((item) => {
                                    this.cdaReportsSignError.set(item.reportId, {
                                        serverError: item.serverError,
                                        serverErrorDetail: item.serverErrorDetail,
                                    });
                                });
                            }
                            return visitAndEcgSignResult;
                        case SignType.SISSCloud: {
                            const token = await this.sissCloudService.getAccessToken(info.sissCloud.signerId);
                            const visitInfo = new SeSignInfo();
                            const ecgInfo = new SeSignInfo();

                            visitInfo.type = SignType.SISSCloud;
                            visitInfo.sissCloud = new SeSissCloudSignInfo(visitSignData);
                            visitInfo.sissCloud.token = token;
                            visitInfo.sissCloud.otp = info.sissCloud.otp;
                            visitInfo.sissCloud.pin = info.sissCloud.pin;
                            ecgInfo.type = SignType.SISSCloud;
                            ecgInfo.sissCloud = new SeSissCloudSignInfo(ecgSignData);
                            ecgInfo.sissCloud.token = token;
                            ecgInfo.sissCloud.otp = info.sissCloud.otp;
                            ecgInfo.sissCloud.pin = info.sissCloud.pin;

                            const results = await firstValueFrom(
                                forkJoin([visitService.sign(visitInfo), ecgService.sign(ecgInfo)]).pipe(
                                    map(([visitsResult, ecgResults]) => {
                                        return visitsResult.concat(ecgResults);
                                    }),
                                ),
                            );

                            if (print) {
                                let signedReportIds = results
                                    .filter((item) => item.signed)
                                    .map((item) => item.reportId);
                                this.printSelectedReports(signedReportIds);
                            }

                            if (results.length) {
                                this._reportContext?.refresh();
                                results.forEach((item) => {
                                    this.cdaReportsSignError.set(item.reportId, {
                                        serverError: item.serverError,
                                        serverErrorDetail: item.serverErrorDetail,
                                    });
                                });
                            }

                            return results;
                        }
                        case SignType.SISSWayCloud: {
                            const token = await this.sissCloudService.getAccessToken(info.sissWayCloud.signerId);
                            const visitInfo = new SeSignInfo();
                            const ecgInfo = new SeSignInfo();

                            visitInfo.type = SignType.SISSWayCloud;
                            visitInfo.sissWayCloud = new SeSissWayCloudSignInfo(visitSignData);
                            visitInfo.sissWayCloud.token = token;
                            visitInfo.sissWayCloud.otp = info.sissWayCloud.otp;
                            visitInfo.sissWayCloud.pin = info.sissWayCloud.pin;
                            ecgInfo.type = SignType.SISSCloud;
                            ecgInfo.sissWayCloud = new SeSissWayCloudSignInfo(ecgSignData);
                            ecgInfo.sissWayCloud.token = token;
                            ecgInfo.sissWayCloud.otp = info.sissWayCloud.otp;
                            ecgInfo.sissWayCloud.pin = info.sissWayCloud.pin;

                            const results = await firstValueFrom(
                                forkJoin([visitService.sign(visitInfo), ecgService.sign(ecgInfo)]).pipe(
                                    map(([visitsResult, ecgResults]) => {
                                        return visitsResult.concat(ecgResults);
                                    }),
                                ),
                            );
                            if (print) {
                                let signedReportIds = results
                                    .filter((item) => item.signed)
                                    .map((item) => item.reportId);
                                this.printSelectedReports(signedReportIds);
                            }

                            if (results.length) {
                                this._reportContext?.refresh();
                                results.forEach((item) => {
                                    this.cdaReportsSignError.set(item.reportId, {
                                        serverError: item.serverError,
                                        serverErrorDetail: item.serverErrorDetail,
                                    });
                                });
                            }

                            return results.flat();
                        }
                        default:
                            return Promise.reject({ message: this.translate.instant('DigitalSign_SignNotSupported') });
                    }
                },
                async (info: SeSignInfo) => {
                    info.isDeferred = !!isDeferred;
                    if (info.info) {
                        info.info.reportIdList = visitSignData.reportIdList;
                    }
                    const ecgPdf = (await firstValueFrom(visitService.getPdf(info))) as ISeSignResultData[];
                    if (info.info) {
                        info.info.reportIdList = ecgSignData.reportIdList;
                    }
                    const visitPdf = (await firstValueFrom(ecgService.getPdf(info))) as ISeSignResultData[];
                    return [...ecgPdf, ...visitPdf];
                },
                (err) => {
                    if (err) {
                        reject(err);
                    } else {
                        resolve();
                    }
                },
            );
        });
    }

    async sign(
        examService: IReportWorkflowExamService,
        signRequestData: SeSignRequestData,
        options: SignOptions,
        fromCda = false,
    ) {
        await this.preFlowOps(examService, signRequestData, options.isDeferred);

        // Operazioni preliminari all'apertura della finestra di firma
        switch (this.signService.currentSignType) {
            case SignType.ScrybaSign:
                let certificates: IScrybaCertificate[] = [];

                this.waitingService.isWaiting = true;
                certificates = await firstValueFrom(
                    this.visitRestService.getScrybaCertificate(signRequestData.signerId),
                );
                this.waitingService.isWaiting = false;
                if (!Array.isArray(certificates) || certificates[0]?.code == 'error') {
                    this.modalDialogNotificationService.error(
                        this.translate.instant('ScrybaSign_CertificatesNotFound'),
                    );
                    if (Array.isArray(certificates) && !!certificates[0]?.description)
                        this.notificationService.error(
                            certificates[0].description,
                            this.translate.instant('ScrybaSign_CertificatesNotFound'),
                        );
                    return Promise.reject<void>(true);
                }

                signRequestData.extraData = certificates;
                break;
            case SignType.Confirmo:
                this.waitingService.isWaiting = true;
                try {
                    let confirmoInfo: SeConfirmoSignInfo = await firstValueFrom(
                        this.visitRestService.getConfirmoSignConfiguration(signRequestData.signerId),
                    );
                    signRequestData.extraData = confirmoInfo;
                } catch {
                    this.modalDialogNotificationService.error(this.translate.instant('Sign_InitializationError'));
                    return Promise.reject<void>(true);
                } finally {
                    this.waitingService.isWaiting = false;
                }

                break;
            case SignType.CompedDigitalSign:
                if (options.addendum) {
                    await this.handleAddendum(examService, options.addendum);
                }
                break;
            default:
                //Do nothing
                break;
        }

        return new Promise<void>((resolve, reject) => {
            this.signService.beginSigningFlow(
                signRequestData,
                async (info: SeSignInfo): Promise<ISeSignResultData[]> => {
                    //comped addendum va gestito prima
                    if (options.addendum && this.signService.currentSignType !== SignType.CompedDigitalSign) {
                        await this.handleAddendum(examService, options.addendum);
                    }
                    // Se non si usa la arrowfunction, this è estensasignservice)
                    info.isDeferred = !!options.isDeferred;
                    if (!!options.displayedSettings) {
                        info.displayedSettings = options.displayedSettings;
                    }

                    switch (this.signService.currentSignType) {
                        case SignType.SISSCloud: {
                            const token = await this.sissCloudService.getAccessToken(info.sissCloud.signerId);
                            info.sissCloud.token = token;
                            break;
                        }
                        case SignType.SISSWayCloud: {
                            const token = await this.sissCloudService.getAccessToken(info.sissWayCloud.signerId);
                            info.sissWayCloud.token = token;
                            break;
                        }
                    }

                    switch (this.signService.currentSignType) {
                        case SignType.SISSWay:
                        case SignType.SISS:
                        case SignType.ARUBA:
                        case SignType.CompedDigitalSign:
                        case SignType.InfoCert:
                        case SignType.Confirmo:
                        case SignType.InsielARSS:
                        case SignType.SISSCloud:
                        case SignType.SISSWayCloud:
                        case SignType.ScrybaSign:
                            return firstValueFrom(examService.sign(info))
                                .then((list: ISeSignResultData[]) => {
                                    /* Qui le operazioni di firma e store sono finite e ho tutte le info sull'esito */

                                    // controllare list

                                    if (options.print) {
                                        let signedReportIds = list
                                            .filter((item) => item.signed)
                                            .map((item) => item.reportId);
                                        this.printSelectedReports(signedReportIds);
                                    }

                                    // Operazione completata con successo
                                    this._reportContext?.refresh(list.map((item) => item.report));

                                    if (list.length && fromCda) {
                                        list.forEach((item) => {
                                            this.cdaReportsSignError.set(item.reportId, {
                                                serverError: item.serverError,
                                                serverErrorDetail: item.serverErrorDetail,
                                            });
                                        });
                                    }
                                    return list;
                                })
                                .catch((err) => {
                                    return err;
                                });
                        default:
                            return Promise.reject({ message: this.translate.instant('DigitalSign_SignNotSupported') });
                    }
                },
                async (info: SeSignInfo) => {
                    info.isDeferred = !!options.isDeferred;
                    return firstValueFrom(examService.getPdf(info)).then((list: ISeSignResultData[]) => {
                        return list;
                    });
                },
                (err) => {
                    if (err) {
                        reject(err);
                    } else {
                        resolve();
                    }
                },
            );
        });
    }

    // private functions

    private async checkAndShowApprovalWarning(reports: IReport[]) {
        const selectedItems = reports.filter((item) => item.isReported && !item.isApproved && !item.isDigitallySigned);
        if (reports.length > 1 && selectedItems.length != reports.length) {
            await firstValueFrom(
                this.modalDialogNotificationService.warning(this.translate.instant('ECG_NotAllReportsCanBeApproved')),
            );
        }
    }

    private handleApproveCredentialReceivedErrors = (approveError: any) => {
        this.logger.error(approveError);

        if (approveError?.message?.startsWith('PARTIAL_')) {
            const data = approveError.message.split('_');

            throw new ApprovalError(this.translate.instant('PartiallyApprovedDocOnTotal'), data[2], data[1], 'warn');
        }

        if (approveError && approveError.message) {
            throw approveError;
        }

        throw new Error(this.translate.instant(this.confirmDialogService.errorMessageKey));
    };

    private async handleLockedExams(
        allLockedExams: IResponseStatus[],
        allReports: IReport[],
        approvedReports: IResponseStatus[],
        allResults: IResponseStatus[],
        print: boolean,
    ) {
        const examsToClose = allLockedExams.map((exam) => exam.error.idExamination);

        const errorMessage =
            this.translate.instant('ExaminationsToApproveAreLocked') +
            ' ' +
            allLockedExams
                .map((examResult) => {
                    const reportInfo = allReports.find((report) => report.id === examResult.id);
                    const userFullName = examResult.error?.userFullName ? `(${examResult.error?.userFullName})` : '';
                    const description = reportInfo?.reportDetailForNotification || examResult.id;
                    return `${description} ${userFullName}`;
                })
                .join(', ');

        const buttonClick = await firstValueFrom(
            this.modalDialogNotificationService.notify({
                buttonCancel: false,
                buttonOk: true,
                buttonYes: false,
                buttonNo: false,
                message: errorMessage,
                title: this.translate.instant('Warning'),
                type: NotificationType.warning,
            }),
        );

        if (buttonClick === ButtonClick.ok) {
            if (examsToClose.length) {
                this.store.dispatch(seLockedExams({ examsToClose }));
            }
        }

        this._reportContext?.refresh(approvedReports.map((item) => item.dto));
        if (print) {
            this.printReports(allResults.map((item) => item.dto));
        }
    }

    private async handleErrorsReports(
        allReports: IReport[],
        allErrors: IResponseStatus[],
        allApproved: IResponseStatus[],
        print: boolean,
    ) {
        if (allErrors.length < allReports.length) {
            // Approve PARZIALE
            // Refresh ed eventuale stampa dei soli approvati
            this._reportContext?.refresh(allApproved.map((item) => item.dto));
            if (print) {
                this.printReports(allApproved.map((item) => item.dto));
            }

            throw new Error('PARTIAL_' + allErrors.length + '_' + allReports.length);
        } else {
            // TUTTI NON FIRMATI
            throw new Error(
                this.translate.instant('PartiallyApprovedDocOnTotal', {
                    p0: allErrors.length,
                    p1: allReports.length,
                }),
            );
        }
    }

    private handleCredentialError(
        allServerErrors: IResponseStatus[],
        allReports: IReport[],
        allErrors: IResponseStatus[],
    ) {
        allServerErrors
            .map((result) => ({
                description:
                    allReports.find((report) => report.id === result.id)?.reportDetailForNotification || result.id,
                errorMessage: this.translate.instant(result.error?.message || ''),
                partialApproveMessage: this.translate.instant('PartiallyApprovedOnTotal', {
                    p0: allErrors.length,
                    p1: allReports.length,
                }),
            }))
            .forEach(({ description, errorMessage, partialApproveMessage }) =>
                this.notificationService.error(`${description} ${errorMessage}`, partialApproveMessage),
            );
    }

    private handleNoErrorResults(response: IResponseStatus[], print: boolean) {
        const dtoList = response.map((res) => res.dto);
        this._reportContext?.refresh(dtoList);
        if (print) {
            this.printReports(dtoList);
        }
    }

    // Operazioni da far partire ancor prima di mostrare la modale di autenticazione, per es. caricamento dei PDF
    //   verso SissCloud.
    private preFlowOps(
        examService: IReportWorkflowExamService,
        signRequestData: SeSignRequestData,
        isDeferred?: boolean,
    ): Promise<void> {
        switch (this.signService.currentSignType) {
            case SignType.SISSCloud:
            case SignType.SISSWayCloud:
                assertIsSissCloudSignRequestData(signRequestData);
                return this.sissCloudService.prepareSign(examService, signRequestData, isDeferred ?? false);
            default:
                return Promise.resolve();
        }
    }

    private async onApproveMassiveCredentialsReceived(params: MassiveApproveParams, authData: AuthData) {
        const {
            print,
            isDeferred,
            addendum,
            displayedSettings,
            isFromWorklist,
            ecgReports,
            visitReports,
            ecgService,
            visitService,
        } = params;
        this._reportContext?.setIsBusy(true);

        // Call for authentication
        if (!isDeferred) {
            await this.checkAndShowApprovalWarning([...ecgReports, ...visitReports]);
        }

        if (addendum) {
            await this.handleAddendum(ecgService, addendum);
            await this.handleAddendum(visitService, addendum);
        }

        let credentialError = false;

        try {
            // Notifico tutti gli errori sul pallino
            this.confirmDialogService.errorMessageKey = 'ErrorDuringApproving'; // Reset del messaggio di errore di default per la finestra di autenticazione

            if (isDeferred) {
                this.confirmDialogService.errorMessageKey = 'ErrorDuringAnnulment';
            }

            const allReports = [...ecgReports, ...visitReports];

            /*ECG*/
            const ecgReportIdsToApprove = ecgReports.map((report) => report.id);
            const ecgResults = await firstValueFrom(
                ecgService.approve(
                    ecgReportIdsToApprove,
                    authData,
                    isDeferred,
                    isFromWorklist,
                    displayedSettings,
                    null,
                    params.userId,
                ),
            );
            const approvedEcgs = ecgResults.filter(isResponseSignOk);
            const erroredEcgs = ecgResults.filter(isResponseSignErrored);
            const lockedEcgs = ecgResults.filter(isResponseSignLocked);
            const ecgServerError = ecgResults.filter(isResponseServerError);

            /*VISIT*/
            const visitReportIdsToApprove = visitReports.map((report) => report.id);
            const visitResults = await firstValueFrom(
                visitService.approve(
                    visitReportIdsToApprove,
                    authData,
                    isDeferred,
                    isFromWorklist,
                    displayedSettings,
                    null,
                    params.userId,
                ),
            );
            const approvedVisits = visitResults.filter(isResponseSignOk);
            const erroredVisits = visitResults.filter(isResponseSignErrored);
            const lockedVisits = visitResults.filter(isResponseSignLocked);
            const visitServerError = visitResults.filter(isResponseServerError);

            /*ALL*/
            const allApproved = [...approvedEcgs, ...approvedVisits];
            const allLockedExams = [...lockedEcgs, ...lockedVisits];
            const allResults = [...ecgResults, ...visitResults];
            const allErrors = [...erroredEcgs, ...erroredVisits];
            /** errori di generazione del pdf dovuti alle troppe immagini significative */
            const allSignificantImagesErrors = [
                ...visitResults.filter(isResponseSignificantImagesError),
                ...ecgResults.filter(isResponseSignificantImagesError),
            ];

            if (isEmpty(erroredEcgs)) {
                this.handleNoErrorResults(ecgResults, print);
            }

            if (isEmpty(erroredVisits)) {
                this.handleNoErrorResults(visitResults, print);
            }

            if (isEmpty(allErrors)) {
                return;
            }

            if (allSignificantImagesErrors.length) {
                /** se ci sono errori di generazione del pdf dovuti alle troppe immagini significative creo un unico messaggio di errore */
                const significantImagesErrorMessage = createSignificantImagesErrorMessage(
                    allSignificantImagesErrors,
                    allReports,
                    this.translate,
                );
                this.notificationService.error(significantImagesErrorMessage);
                this.modalDialogNotificationService.error(significantImagesErrorMessage);
            }

            const allServerErrors = [...visitServerError, ...ecgServerError];
            credentialError = Boolean(allServerErrors.find(isExceptionCredentialFault)) || false;

            if (credentialError) {
                this.confirmDialogService.errorMessageKey = 'Exception_CredentialsFault';
                this.handleCredentialError(allServerErrors, allReports, allErrors);
            } else if (allLockedExams.length > 0) {
                return this.handleLockedExams(allLockedExams, allReports, allApproved, allResults, print);
            }

            /*il throw nel metodo seguente viene gestito dal catch qui sotto*/
            await this.handleErrorsReports(allReports, allErrors, allApproved, print);
        } catch (approveError: any) {
            if (credentialError) {
                throw new Error(this.translate.instant(this.confirmDialogService.errorMessageKey));
            }
            this.handleApproveCredentialReceivedErrors(approveError);
        } finally {
            this._reportContext?.setIsBusy(false);
        }
    }

    /**
     * call back usata in approve per personalizzare il comportamento dei confirm-dialog
     * @param examService
     * @param authData
     * @param reports
     * @param print
     * @param isDeferred
     * @param addendum
     * @param isFromWorklist
     * @param displayedSettings
     * @private
     */
    private async onApproveCredentialsReceived(
        examService: IReportWorkflowExamService,
        authData: AuthData,
        reports: IReport[],
        print: boolean,
        isDeferred: boolean,
        addendum?: Addendum,
        isFromWorklist?: boolean,
        displayedSettings?: EcgChartState,
        referringPhysicianId?: string,
        isReportFromDDevice?: boolean,
    ) {
        this._reportContext?.setIsBusy(true);

        // Call for authentication

        if (!isDeferred) {
            //i report con DDevice possono anche non essere refertati
            const selectedItems = (isReportFromDDevice && referringPhysicianId) ?
                reports.filter((item) => !item.isApproved && !item.isDigitallySigned) :
                reports.filter((item) => item.isReported && !item.isApproved && !item.isDigitallySigned);
            if (reports.length > 1 && selectedItems.length != reports.length) {
                await firstValueFrom(
                    this.modalDialogNotificationService.warning(
                        this.translate.instant('ECG_NotAllReportsCanBeApproved'),
                    ),
                );
            }
        }

        if (addendum) {
            await this.handleAddendum(examService, addendum);
        }

        try {
            // Notifico tutti gli errori sul pallino
            this.confirmDialogService.errorMessageKey = 'ErrorDuringApproving'; // Reset del messaggio di errore di default per la finestra di autenticazione

            if (isDeferred) {
                this.confirmDialogService.errorMessageKey = 'ErrorDuringAnnulment';
            }

            let reportsToApprove = reports.map((item) => item.id);
            let results = await firstValueFrom(
                examService.approve(
                    reportsToApprove,
                    authData,
                    isDeferred,
                    isFromWorklist,
                    displayedSettings,
                    null,
                    referringPhysicianId,
                ),
            );

            const approvedReports = results.filter(isResponseSignOk);
            const errors = results.filter(isResponseSignErrored);
            const lockedExams = results.filter(isResponseSignLocked);
            const serverErrors = results.filter(isResponseServerError);
            /** errori di generazione del pdf dovuti alle troppe immagini significative */
            const significantImagesErrors = results.filter(isResponseSignificantImagesError);

            let errorMessage = ' ';
            let examsToClose: string[] = [];

            if (isEmpty(errors)) {
                // Operazione completata con successo
                this._reportContext?.refresh(results.map((item) => item.dto));
                if (print) {
                    this.printReports(results.map((item) => item.dto));
                }
                return;
            } else {
                let credentialError = false;
                if (serverErrors && serverErrors.length > 0) {
                    serverErrors.forEach((e) => {
                        // Messaggio di errore non generico
                        if (e && e.error && e.error.message && e.error.message == 'Exception_CredentialsFault') {
                            this.confirmDialogService.errorMessageKey = 'Exception_CredentialsFault';
                            credentialError = true;
                        }

                        let reportInfo = reports.find((item) => item.id == e.id);
                        let reportDescr =
                            reportInfo && reportInfo.reportDetailForNotification
                                ? reportInfo.reportDetailForNotification
                                : e.id;

                        // messaggino in fondo
                        this.notificationService.error(
                            this.translate.instant('Report') +
                                ': ' +
                                reportDescr +
                                ' - ' +
                                (e.error && e.error.message ? this.translate.instant(e.error.message) : ''),
                            this.translate.instant('PartiallyApprovedDocOnTotal', {
                                p0: errors.length,
                                p1: reportsToApprove.length,
                            }),
                        );
                    });
                }
                if (significantImagesErrors.length) {
                    /** se ci sono errori di generazione del pdf dovuti alle troppe immagini significative creo un unico messaggio di errore e lo notifico */
                    const significantImagesErrorMessage = createSignificantImagesErrorMessage(
                        significantImagesErrors,
                        reports,
                        this.translate,
                    );
                    this.notificationService.error(significantImagesErrorMessage);
                    this.modalDialogNotificationService.error(significantImagesErrorMessage);
                }
                if (lockedExams && lockedExams.length > 0 && !credentialError) {
                    // this.confirmDialogService.cancelConfirmFlow();

                    errorMessage += this.translate.instant('ExaminationsToApproveAreLocked') + ' ';
                    lockedExams.forEach((exam) => {
                        let reportInfo = reports.find((item) => item.id == exam.id);
                        const userFullName = exam.error?.userFullName ? ' (' + exam.error.userFullName + ')' : '';
                        let reportDescr =
                            reportInfo && reportInfo.reportDetailForNotification
                                ? reportInfo.reportDetailForNotification
                                : exam.id;
                        errorMessage += reportDescr + userFullName + ', ';
                        examsToClose.push(exam.error.idExamination);
                    });
                    errorMessage = errorMessage.slice(0, -2);

                    let buttonClick = await firstValueFrom(
                        this.modalDialogNotificationService.notify({
                            buttonCancel: false,
                            buttonOk: true,
                            buttonYes: false,
                            buttonNo: false,
                            message: errorMessage,
                            title: this.translate.instant('Warning'),
                            type: NotificationType.warning,
                        }),
                    );
                    if (buttonClick === ButtonClick.ok) {
                        if (examsToClose.length) {
                            this.store.dispatch(seLockedExams({ examsToClose }));
                        }
                    }

                    this._reportContext?.refresh(approvedReports.map((item) => item.dto));
                    if (print) {
                        this.printReports(results.map((item) => item.dto));
                    }

                    return;
                }

                // qui non entra se ci sono stati lockati.. ma fa vedere il messaggio prima dei server errors
                if (errors.length < reportsToApprove.length) {
                    // Approve PARZIALE
                    // Refresh ed eventuale stampa dei soli approvati

                    this._reportContext?.refresh(approvedReports.map((item) => item.dto));
                    if (print) {
                        this.printReports(approvedReports.map((item) => item.dto));
                    }

                    throw new Error('PARTIAL_' + errors.length + '_' + reportsToApprove.length);
                } else {
                    // TUTTI NON FIRMATI
                    throw new Error(
                        this.translate.instant('PartiallyApprovedDocOnTotal', {
                            p0: errors.length,
                            p1: reportsToApprove.length,
                        }),
                    );
                }
            }
        } catch (e: any) {
            this.logger.error(e);

            if (e?.message?.startsWith('PARTIAL_')) {
                const data = e.message.split('_');

                throw new ApprovalError(
                    this.translate.instant('PartiallyApprovedDocOnTotal'),
                    data[2],
                    data[1],
                    'warn',
                );
            }

            if (e && e.message) {
                throw e;
            }

            throw new Error(this.translate.instant(this.confirmDialogService.errorMessageKey));
        } finally {
            this._reportContext?.setIsBusy(false);
        }
    }

    //TODO: (34.12) ???
    private async onApproveMultiCredentialsReceived(
        examService: IReportWorkflowExamService,
        authData: AuthData[],
        _reports: IReport[],
        print: boolean,
        isDeferred: boolean,
        addendum?: Addendum,
        isFromWorklist?: boolean,
        displayedSettings?: EcgChartState,
    ) {
        this._reportContext?.setIsBusy(true);
        /*83782 nel caso multi il referto deve essere solamente 1 sollevare errore qui? oppure limitare la cosa durante la selezione degli esami?
         * per ora facciamo slice lasciando solo il primo report*/
        const reports = _reports.length ? [_reports[0]] : [];

        // Call for authentication
        if (!isDeferred) {
            const selectedItems = reports.filter(
                (item) => item.isReported && !item.isApproved && !item.isDigitallySigned,
            );
            if (reports.length < 1 || selectedItems.length != reports.length) {
                await firstValueFrom(
                    this.modalDialogNotificationService.warning(
                        this.translate.instant('ECG_NotAllReportsCanBeApproved'),
                    ),
                );
            }
        }

        if (addendum) {
            await this.handleAddendum(examService, addendum);
        }

        try {
            // Notifico tutti gli errori sul pallino
            this.confirmDialogService.errorMessageKey = 'ErrorDuringApproving'; // Reset del messaggio di errore di default per la finestra di autenticazione

            if (isDeferred) {
                this.confirmDialogService.errorMessageKey = 'ErrorDuringAnnulment';
            }

            const reportsToApprove = reports.map((item) => item.id);

            const results = await firstValueFrom(
                examService.approve(
                    reportsToApprove,
                    authData[0],
                    isDeferred,
                    isFromWorklist,
                    displayedSettings,
                    authData.slice(1),
                ),
            );

            const signErrors = results.filter((result) => result.responseStatusType != SignResultStatus.Ok);
            /** errori di generazione del pdf dovuti alle troppe immagini significative */
            const significantImagesErrors = results.filter(isResponseSignificantImagesError);

            if (isEmpty(signErrors) /*&& isEmpty(serverErrors)*/) {
                // Operazione completata con successo
                this._reportContext?.refresh(results.map((item) => item.dto));
                if (print) {
                    this.printReports(results.map((item) => item.dto));
                }
                return;
            }

            if (significantImagesErrors.length) {
                /** se ci sono errori di generazione del pdf dovuti alle troppe immagini significative creo un unico messaggio di errore */
                const significantImagesErrorMessage = createSignificantImagesErrorMessage(
                    significantImagesErrors,
                    reports,
                    this.translate,
                );
                this.notificationService.error(significantImagesErrorMessage);
                this.modalDialogNotificationService.error(significantImagesErrorMessage);
            } else {
                const credentialErrors = results.some((res) => res.error.message === 'Exception_CredentialsFault');
                if (credentialErrors) this.confirmDialogService.errorMessageKey = 'Exception_CredentialsFault';
                this.notificationService.error(
                    this.translate.instant('Report') +
                        ': ' +
                        reports[0].reportDetailForNotification +
                        ' - ' +
                        this.translate.instant('PartiallyApprovedDocOnTotal', {
                            p0: signErrors.length,
                            p1: reportsToApprove.length,
                        }),
                );
            }
            throw new Error(JSON.stringify(results));
        } catch (err: any) {
            if (err?.message?.startsWith('PARTIAL_')) {
                const data = err.message.split('_');

                throw new ApprovalError(
                    this.translate.instant('PartiallyApprovedDocOnTotal'),
                    data[2],
                    data[1],
                    'warn',
                );
            }

            if (err && err.message) {
                throw err;
            }

            throw new Error(this.translate.instant(this.confirmDialogService.errorMessageKey));
        } finally {
            this._reportContext?.setIsBusy(false);
        }
    }

    //TODO: (34.12) ???
    private async onUndoValidationMultiCredentialsReceived(
        examService: IReportWorkflowExamService,
        authData: AuthData[],
        reports: IReport[],
        isFromWorklist: boolean,
        isFromCda: boolean = false,
    ) {
        this._reportContext?.setIsBusy(true);

        try {
            this.confirmDialogService.errorMessageKey = 'UndoValidationOperationError';
            const reportsIds = reports.map((report) => report.id);

            let result;

            if (isFromCda && this._authData) {
                result = await firstValueFrom(
                    examService.undoMultiValidation(reportsIds[0], this._authData as AuthData[], isFromWorklist),
                );
            } else {
                result = await firstValueFrom(examService.undoMultiValidation(reportsIds[0], authData, isFromWorklist));
            }

            const results = [result];
            const signErrors = results.filter((result) => result.responseStatusType != SignResultStatus.Ok);

            if (/*result.responseStatusType === SignResultStatus.Ok*/ isEmpty(signErrors)) {
                if (isFromCda) {
                    this._authData = authData;
                }
                this._reportContext?.refresh(results.map((item) => item.dto));
                return;
            } else {
                const credentialErrors = results.some((res) => res.error.message === 'Exception_CredentialsFault');
                if (credentialErrors) this.confirmDialogService.errorMessageKey = 'Exception_CredentialsFault';

                let reportInfo = reports[0];
                let reportDescr =
                    reportInfo && reportInfo.reportDetailForNotification
                        ? reportInfo.reportDetailForNotification
                        : reportInfo.id;

                const errorMessage = results
                    .map((result) => result?.error?.message || '')
                    .filter(Boolean)
                    .join('.');
                this.notificationService.error(
                    this.translate.instant('Report') +
                        ': ' +
                        reportDescr +
                        ' - ' +
                        this.translate.instant(errorMessage),
                );

                this._authData = null;
                throw new Error(JSON.stringify(results));
            }
        } catch (e: any) {
            console.error(e);
            const err = e?.message || '{}';
            throw new Error(err);
        } finally {
            this._reportContext?.setIsBusy(false);
        }
    }

    private async onUndoValidationCredentialsReceived(
        examService: IReportWorkflowExamService,
        authData: AuthData,
        reports: IReport[],
        reportId: string,
        isFromWorklist: boolean,
        isFromCda: boolean = false,
    ) {
        this._reportContext?.setIsBusy(true);
        let result: any;

        try {
            this.confirmDialogService.errorMessageKey = 'UndoValidationOperationError';

            if (isFromCda && this._authData) {
                result = await firstValueFrom(
                    examService.undoValidation(reportId, this._authData as AuthData, isFromWorklist),
                );
            } else {
                result = await firstValueFrom(examService.undoValidation(reportId, authData, isFromWorklist));
            }

            // let errorMessage = '';
            let examsToClose = [];

            if (result.responseStatusType === SignResultStatus.Ok) {
                if (isFromCda) {
                    this._authData = authData;
                    this.cdaReports.set(reportId, {
                        isUndoSuccessfull: true,
                        reportDetailsForNotification: reports[0].reportDetailForNotification,
                    });
                }
                this._reportContext?.refresh([result.dto]);
                return;
            } else {
                if (
                    [
                        SignResultStatus.Failed,
                        SignResultStatus.PdfGenerationFailed,
                        SignResultStatus.P7mGenerationFailed,
                    ].includes(result.responseStatusType)
                ) {
                    // Messaggio di errore non generico che compare sulla finestra
                    if (result.error && result.error.message && result.error.message == 'Exception_CredentialsFault') {
                        this.confirmDialogService.errorMessageKey = 'Exception_CredentialsFault';
                    }

                    let reportInfo = reports.find((item) => item.id == result.id);
                    let reportDescr =
                        reportInfo && reportInfo.reportDetailForNotification
                            ? reportInfo.reportDetailForNotification
                            : result.id;

                    throw new Error(
                        this.translate.instant('Report') +
                            ': ' +
                            reportDescr +
                            ' - ' +
                            (result.error && result.error.message ? this.translate.instant(result.error.message) : ''),
                    );
                } else if ([SignResultStatus.Locked, SignResultStatus.Unlocked].includes(result.responseStatusType)) {
                    const userFullName = result.error?.userFullName ? ' (' + result.error.userFullName + ')' : '';
                    let errorMessage = this.translate.instant('ExaminationsToUnApproveAreLocked') + userFullName;
                    examsToClose.push(result.error.idExamination);

                    let buttonClick = await firstValueFrom(
                        this.modalDialogNotificationService.notify({
                            buttonCancel: false,
                            buttonOk: true,
                            buttonYes: false,
                            buttonNo: false,
                            message: errorMessage,
                            title: this.translate.instant('Warning'),
                            type: NotificationType.warning,
                        }),
                    );
                    if (buttonClick === ButtonClick.ok) {
                        if (examsToClose.length) {
                            this.store.dispatch(seLockedExams({ examsToClose }));
                        }
                    }
                }
                this.cdaReports.set(reportId, { isUndoSuccessfull: false, reportDetailsForNotification: '' });
                this._authData = null;
            }
        } catch (e: any) {
            throw new Error(
                e && e.message ? e.message : this.translate.instant(this.confirmDialogService.errorMessageKey),
            );
        } finally {
            this._reportContext?.setIsBusy(false);
        }
    }

    private printSelectedReports(reportIds: string[], displayedSettings?: any) {
        this.store.dispatch(sePrintEcgPdfOnModal({ reportIds, displayedSettings })); // Notifico lo stato
    }

    private async handleAddendum(examService: IReportWorkflowExamService, addendum: Addendum) {
        // Deleting
        if (addendum.id && !addendum.isDeleting) {
            await firstValueFrom(examService.deleteAddendum(addendum));
            addendum.isDeleting = true;
        }
        // Creating
        if (!addendum.id && !addendum.isDraft) {
            await firstValueFrom(examService.saveAddendum(addendum));
            addendum.isDraft = true;
        }
    }

    private async buildSissDataSign(
        examService: IReportWorkflowExamService,
        signer: {
            id: string;
            name: string;
        },
        patientReportPair: IPatientReportPair[],
        reportIdList: string[],
        reportErrorDetailList: { id: string; message: string }[],
    ) {
        const data: SeSissSignRequestData = {
            signerName: signer.name,
            signerId: signer.id,
            reportIdList,
            reportErrorDetailList,
            sissDataInfoList: [],
        };

        for (let i = 0; i < patientReportPair.length; i++) {
            let pair = patientReportPair[i];
            let idExamination = pair.idExamination;
            let isLocalPatient = pair.isLocalPatient;
            let errorMsg: string;

            this.waitingService.isWaiting = true;
            let sissData: SissData | null = await firstValueFrom(examService.getDataForSissSign(idExamination || ''))
                .catch((e) => {
                    errorMsg = e?.error?.estensaFaultDetails?.estensaFault?.message;
                    if (!errorMsg) errorMsg = '';

                    this.notificationService.error(
                        errorMsg,
                        'Impossibile scaricare le informazioni necessarie per la firma SISS o SISSWAY',
                    );
                    return null;
                })
                .finally(() => {
                    this.waitingService.isWaiting = false;
                });

            if (!sissData || !sissData.patient) {
                this.modalDialogNotificationService.error(
                    this.translate.instant('Exception_SignFault', {
                        p0: 'Impossibile scaricare le informazioni necessarie per la firma SISS o SISSWAY',
                    }),
                );
                return null;
            }

            for (let n = 0; n < pair.idReportList.length; n++) {
                let idReport = pair.idReportList[n];

                data.sissDataInfoList.push({
                    patient: {
                        id: pair.patientId,
                        birthDate: sissData.patient.birthDate
                            ? moment(sissData.patient.birthDate).utc().toISOString()
                            : '',
                        healthCard: sissData.patient.healthCard ? sissData.patient.healthCard : '',
                        name: sissData.patient.name ? sissData.patient.name : '',
                        surname: sissData.patient.surname ? sissData.patient.surname : '',
                        sexCode: sissData.patient.sexCode ? sissData.patient.sexCode : '',
                        taxCode: sissData.patient.taxCode ? sissData.patient.taxCode : '',
                        isLocalPatient: isLocalPatient,
                    },
                    obscuration: {
                        obsDrug: sissData.obscuration.obsDrug,
                        obsHiv: sissData.obscuration.obsHiv,
                        obsViolence: sissData.obscuration.obsViolence,
                        obsInterruption: sissData.obscuration.obsInterruption,
                        obsVoluntary: sissData.obscuration.obsVoluntary,
                        obsAvailability: sissData.obscuration.obsAvailability,
                        consultationAuthorized: sissData.obscuration.consultationAuthorized,
                    },
                    report: [
                        {
                            id: idReport,
                            admissionDateHospitalization: sissData.examination.admissionDate
                                ? moment(sissData.examination.admissionDate).utc().toISOString()
                                : '',
                            codesCardioPerform: [sissData.examination.examCode || ''],
                            fileNumberER: sissData.examination.fileNumberER,
                            nosologicNumberHospitalization: sissData.examination.nosologicNumberHospitalization,
                            reportType: ReportType.SpecialisticRadiologicalReport,
                            idPrestazioneSISS: sissData.examination.idPrestazioneSISS,
                        },
                    ],
                });
            }
        }

        return data;
    }

    /**
     * Questa funzione al momento non serve perché le proprietà extra di SeSissCloudSignRequestData sono state accorpate
     *   a quelle presenti in SeSissSignRequestData
     */
    private async buildSissCloudDataSign(
        examService: IReportWorkflowExamService,
        signer: {
            id: string;
            name: string;
        },
        patientReportPair: IPatientReportPair[],
        reportIdList: string[],
        reportErrorDetailList: { id: string; message: string }[],
    ) {
        const sissData = await this.buildSissDataSign(
            examService,
            signer,
            patientReportPair,
            reportIdList,
            reportErrorDetailList,
        );

        const sissCloudData: SeSissCloudSignRequestData = sissData;

        return sissCloudData;
    }

    async suspend(examService: IReportWorkflowExamService, reportIds: string[], suspensionReasonId: string) {
        this.waitingService.isWaiting = true;
        examService.suspend?.(reportIds, suspensionReasonId).subscribe({
            next: () => {
                this.manageSuccessNotificationAndModal(ToolbarAction.SUSPEND);
                if (this._reportContext) {
                    this._reportContext.refresh();
                }
            },
            error: (e) => {
                this.manageErrorNotificationAndModal(ToolbarAction.SUSPEND, e);
            },
            complete: () => {
                this.waitingService.isWaiting = false;
            },
        });
    }

    async undoSuspension(examService: IReportWorkflowExamService, reportIds: string[]) {
        this.waitingService.isWaiting = true;
        examService.undoSuspension?.(reportIds).subscribe({
            next: () => {
                this.manageSuccessNotificationAndModal(ToolbarAction.UNDO_SUSPENSION);
                if (this._reportContext) {
                    this._reportContext.refresh();
                }
            },
            error: (e) => {
                this.manageErrorNotificationAndModal(ToolbarAction.UNDO_SUSPENSION, e);
            },
            complete: () => {
                this.waitingService.isWaiting = false;
            },
        });
    }

    private manageErrorNotificationAndModal(action: ToolbarAction | null, error: any) {
        let message: string = '';
        let title: string = '';
        switch (action) {
            case 'SuspendExamination':
                message = 'SuspendOperationError';
                break;
            case 'UndoSuspension':
                message = 'UndoSuspendOperationError';
                break;
            default:
                message = 'GenericExecutionFailure';
        }
        this.modalDialogNotificationService.error(this.translate.instant(message), title);
        this.notificationService.error(error.message ? error.message : '', this.translate.instant(message));
    }

    private manageSuccessNotificationAndModal(action: ToolbarAction | null) {
        let message: string = '';
        let title: string = '';
        switch (action) {
            case 'SuspendExamination':
                message = 'SuspendOperationSuccess';
                break;
            case 'UndoSuspension':
                message = 'UndoSuspendOperationSuccess';
                break;
            default:
                message = 'SuccessOperation';
        }
        this.modalDialogNotificationService.success(this.translate.instant(message), title);
        this.notificationService.success(this.translate.instant(message));
    }

    validatedAndSignedReports(
        items: (VisitExamination | ISpecialtyExamination | EcgExamination)[],
    ): (VisitExamination | ISpecialtyExamination | EcgExamination)[] {
        return items
            .filter((item) => {
                if (this.isVisitOrEcgExamination(item)) {
                    return item.isApproved || item.isDigitallySigned;
                } else if (this.isSpecialtyExamination(item)) {
                    return item.command?.canBeUndoApproved;
                }
                return false;
            })
            .map((item) => ({ ...item, isUndoSuccessfull: false }));
    }

    private isVisitOrEcgExamination(item: any): item is VisitExamination | EcgExamination {
        return 'isApproved' in item || 'isDigitallySigned' in item;
    }

    private isSpecialtyExamination(item: any): item is ISpecialtyExamination {
        return 'command' in item && typeof item.command?.canBeUndoApproved !== 'undefined';
    }

    onlyReported(
        items: (VisitExamination | ISpecialtyExamination | EcgExamination)[],
    ): (VisitExamination | ISpecialtyExamination | EcgExamination)[] {
        return items.filter((item) => {
            if (this.isVisitOrEcgExaminationReports(item)) {
                return item.isReported && !item.isApproved && !item.isDigitallySigned;
            } else if (this.isSpecialtyExaminationReports(item)) {
                return item.command?.canBeApproved;
            }
            return false;
        });
    }

    // Type guard per VisitExamination o EcgExamination
    private isVisitOrEcgExaminationReports(item: any): item is VisitExamination | EcgExamination {
        return 'isReported' in item && 'isApproved' in item && 'isDigitallySigned' in item;
    }

    // Type guard per ISpecialtyExamination
    private isSpecialtyExaminationReports(item: any): item is ISpecialtyExamination {
        return 'command' in item && typeof item.command?.canBeApproved !== 'undefined';
    }

    assignIsUndoSuccessfull(items: any[]) {
        items.map((el) => {
            let value = this.cdaReports.get(el.idReport);
            el.isUndoSuccessfull = value?.isUndoSuccessfull;
        });
    }
}
